import { envConfig } from '#constants/envConfig';

/**
 * Единая точка «какая у нас версия».
 *
 * До этого `App.getInfo()` вызывался прямо в компоненте (экран «О приложении»), а номер сборки
 * в остальных местах приходилось таскать через `DeviceSettingsPlugin.getInfo()` — то есть через
 * андроидный плагин, которого на iOS нет. Здесь одна асинхронная выборка на весь сеанс.
 *
 * Плагин грузим динамическим импортом под `IS_NATIVE` в форме `=== 'true'` — константа
 * сворачивается, и в веб-бандл `@capacitor/app` не попадает вовсе.
 */

type NativeInfo = {
  /** versionCode на Android, CFBundleVersion на iOS. Стор оперирует именно им. */
  build: number;
  /** versionName — то, что видит человек («12.0.0»). */
  version: string;
  /** Платформа оболочки. По строке WebView на iOS приложение от Safari не отличить — называем себя сами. */
  platform?: 'android' | 'ios' | 'web';
  /** Производитель телефона (android-плагин). На iOS пусто. */
  oem?: string;
};

/** undefined = ещё не спрашивали; null = спрашивали, ответа нет (веб или плагин недоступен). */
let cached: NativeInfo | null | undefined;
let loading: Promise<void> | null = null;

const load = (): Promise<void> => {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = (async () => {
      try {
        const { App } = await import('@capacitor/app');
        // Зовём МЕТОД плагина, а не возвращаем сам объект: `registerPlugin` отдаёт Proxy с
        // перехватом любого свойства, включая `.then`, и такой объект нельзя делать значением
        // промиса — рантайм примет его за thenable (разбор в `deviceSettings.ts`).
        const info = await App.getInfo();
        const build = Number.parseInt(info.build, 10);
        cached = {
          build: Number.isFinite(build) ? build : 0,
          version: info.version ?? '',
          platform: await readPlatform(),
          oem: await readOem(),
        };
      } catch {
        // Плагина нет или он молчит. Это не ошибка: просто версии не знаем, и все проверки,
        // которые на неё опираются, обязаны в этом случае пропускать (fail-open).
        cached = null;
      }
    })();
  }
  return loading;
};

/** Платформа оболочки; без неё сборка остаётся «приложением без платформы», это не ошибка. */
const readPlatform = async (): Promise<NativeInfo['platform']> => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    const platform = Capacitor.getPlatform();
    return platform === 'ios' ? 'ios' : platform === 'android' ? 'android' : 'web';
  } catch {
    return undefined;
  }
};

/** Производитель — из android-плагина настроек; на iOS его нет, и это нормально. */
const readOem = async (): Promise<string | undefined> => {
  try {
    const { getDeviceInfo } = await import('#features/notifications/utils/deviceSettings');
    const manufacturer = (await getDeviceInfo())?.manufacturer;
    return manufacturer || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Номер сборки из стора. `0` — «не знаю»: веб, плагин недоступен, сборка вне стора.
 * Ноль обязан трактоваться вызывающим как «проверить нельзя», а не как «очень старая версия».
 */
export const getAppBuild = async (): Promise<number> => {
  await load();
  return cached?.build ?? 0;
};

/** Человекочитаемая версия сборки. В вебе — версия из package.json. */
export const getAppVersionName = async (): Promise<string> => {
  await load();
  return cached?.version || import.meta.env.PACKAGE_VERSION;
};

/**
 * Значение заголовка `tm-client`: по нему в access-логе nginx видно реальное распределение
 * версий по ВСЕМ клиентам. До этого версия приложения доезжала до бэкенда единственным путём —
 * вместе с регистрацией push-токена, то есть меньше чем у 2% строк и только на Android.
 *
 * Синхронная функция: заголовки собираются на каждый запрос, и лишний await там не нужен.
 * Пока нативная версия не прочиталась, уходит веб-часть — она известна на этапе сборки.
 */
export const getClientTag = (): string =>
  formatClientTag(`${import.meta.env.PACKAGE_VERSION}+${import.meta.env.BUILD_SHA}`, cached);

/**
 * Всё, что не влезает в заголовок HTTP, вырезается.
 *
 * Значение заголовка обязано быть ISO-8859-1: на любом символе вне этого набора конструктор
 * `Headers` бросает `TypeError`, и падает не один заголовок, а ВЕСЬ запрос. Так у человека с
 * телефоном Omıx (турецкая «ı», U+0131, — она есть в проде) приложение переставало работать
 * целиком: каждый GraphQL-запрос отваливался ещё до отправки, а вместе с ними и регистрация
 * push-токена. В GlitchTip это выглядело как 64 ошибки подряд у одного устройства.
 *
 * Оставляем печатный ASCII и только его: имя производителя — это метка для лога, и потерять в
 * ней букву несравнимо дешевле, чем неработающее приложение.
 */
const headerSafe = (value: string): string => value.replaceAll(/[^\u0020-\u007e]+/g, '');

/**
 * Сборка тега: `<веб-версия>[;build=12][;platform=android][;oem=xiaomi]`.
 * Части после версии — только у нативной оболочки; формат `версия;build=N` для старых
 * читателей (access-лог nginx) не меняется, новые пары дописываются в хвост. Шлюз разбирает
 * тег в реестр сеансов, поэтому в значениях не бывает `;` и `=`.
 */
export const formatClientTag = (
  web: string,
  info: Pick<NativeInfo, 'build' | 'platform' | 'oem'> | null | undefined
): string => {
  if (!info) {
    return web;
  }
  const parts = [web, `build=${info.build}`];
  if (info.platform) {
    parts.push(`platform=${info.platform}`);
  }
  if (info.oem) {
    const oem = headerSafe(info.oem.toLowerCase())
      .replaceAll(/[;=\s]+/g, ' ')
      .trim()
      .slice(0, 40);
    if (oem) {
      parts.push(`oem=${oem}`);
    }
  }
  // Чистим и собранную строку: веб-версия и хеш сборки приходят из окружения, и полагаться на
  // то, что там всегда ASCII, — то же самое допущение, на котором мы уже обожглись.
  return headerSafe(parts.join(';'));
};

/**
 * Номер сборки без ожидания, из уже прочитанного. `0`, если ещё не спрашивали или это веб.
 * Нужен там, где await неуместен: заголовки запроса, контекст баг-репорта.
 */
export const getCachedBuild = (): number => cached?.build ?? 0;

/**
 * Перечитать номер сборки у системы.
 *
 * Кеш выше живёт весь сеанс, и обычно это верно: версия приложения внутри одного процесса не
 * меняется. Но ровно один случай ломает это допущение — Android заменил APK, а процесс не убил.
 * Человек уходит обновляться в Play, возвращается, и приложение продолжает считать себя старым:
 * баннер зовёт на версию, которая уже стоит, а кнопка ведёт в магазин, где написано «Открыть».
 * Выглядит как сломанный продукт, и поймано это было именно так, на живом телефоне.
 *
 * Поэтому номер можно перечитать. Вызов локальный (PackageManager, без сети), и звать его есть
 * смысл при возвращении приложения на экран.
 *
 * Кеш при этом НЕ сбрасывается в «не знаю»: значение заменяется только после удачного ответа.
 * Сброс означал бы окно, в котором {@link getClientTag} — он синхронный и зовётся на каждый
 * запрос — уходил бы без номера сборки, и распределение версий в логах ложно проседало бы.
 */
export const refreshAppBuild = async (): Promise<number> => {
  await load();
  if (!cached) {
    return 0;
  }
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    const build = Number.parseInt(info.build, 10);
    if (!Number.isFinite(build)) {
      return cached.build;
    }
    cached = { ...cached, build, version: info.version || cached.version };
    return build;
  } catch {
    // Плагин молчит — остаёмся при том, что знали. Это не повод объявлять версию неизвестной.
    return cached.build;
  }
};

/** Только для тестов: кеш живёт в модуле и переживает отдельные кейсы. */
export const resetAppVersionCache = () => {
  cached = undefined;
  loading = null;
};
